import React, { useEffect, useState, useRef } from 'react';
import {
  BubbleInput,
  BubbleButton,
  BubbleTextArea,
  BubbleBadge,
  BubbleAlert,
  BubbleDebounce,
  BubbleOverlay,
} from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { Editor } from '@go-client/components';
import {
  ContentTemplateTransforms,
  ContentTemplateTranslatable,
  ContentTemplateTranslation,
} from '@go-shared/types/translation';
import { replaceTranslationByRefer } from '@go-client/lib/api/translation';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { RouteComponentProps, Prompt } from 'react-router-dom';

import { ContentTemplate } from '@go-shared/types/contentTemplate';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './ContentLibraryTranslations.scss';
import {
  CommonTranslationFilters,
  generateDefaultNewTranslation,
  makeContentTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import striptags from 'striptags';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { getMultiPropertyKind } from '@go-shared/lib/libraries';
import { canModifyMultiPropertyItem } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { isNotBasicContentTemplate } from '@go-shared/lib/contentTemplate';
import { DEFAULT_PER_PAGE, PaginationMetadata } from '@go-shared/types/pagination';
import { paginatePropertyContentTemplates } from '@go-client/lib/api/contentTemplate';
import { PaginateTranslations } from '@go-client/components/Translations/PaginateTranslations';

interface ContentLibraryTranslationDataBundle {
  contentTemplates: ContentTemplate[];
}

interface ContentLibraryTranslationForm {
  dirty: boolean;
  transforms: ContentTemplateTransforms;
}

interface ChangeContentTemplateFormAction {
  active?: boolean;
  valueChange?: { key: keyof ContentTemplateTranslatable; value: string };
}

type ContentLibraryTranslationsPageProps = RouteComponentProps;

export default function ContentLibraryTranslations(props: ContentLibraryTranslationsPageProps) {
  const { activeProperty } = useActiveProperty();
  const serverInfo = useServerInfo();
  const { languages } = serverInfo;
  const { user } = useUserInfo();

  const [loadingTranslations, setLoadingTranslations] = useState(true);
  const [loading, setLoading] = useState(true);

  const [dataBundle, setDataBundle] = useState<ContentLibraryTranslationDataBundle | undefined>(
    undefined
  );
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );

  const [activeCntTmpl, setActiveCntTmpl] = useState<ContentTemplate | undefined>(undefined);
  const [cntTmplTranslForm, setCntTmplTranslForm] = useState<
    ContentLibraryTranslationForm | undefined
  >(undefined);

  const [isAdvanced, setIsAdvanced] = useState(false);

  const [paginationFilters, setPaginationFilters] = useState<CommonTranslationFilters>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    createdFrom: '',
    createdTo: '',
    propertyId: activeProperty._id,
    translated: undefined,
    published: undefined,
  });

  const contentRef = useRef<HTMLDivElement | null>(null);
  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationFilters.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationFilters, contentRef]);

  useEffect(() => {
    if (activeProperty._id !== paginationFilters.propertyId) {
      setPaginationFilters({ ...paginationFilters, propertyId: activeProperty._id });
    }
  }, [activeProperty]);

  async function refresh() {
    setLoadingTranslations(true);
    await fetchDataBundle();
  }

  // this will refresh the currently active venue template if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;
    setActiveCntTmpl(state => {
      const defaultCntTmpl = dataBundle.contentTemplates[0];
      if (!state) return defaultCntTmpl;
      const refreshedCntTmpl = dataBundle.contentTemplates.find(ct => ct._id === state._id);
      return refreshedCntTmpl || defaultCntTmpl;
    });
  }, [dataBundle]);

  useEffect(() => {
    refresh();
  }, [paginationFilters]);

  // this will reset the form each time a new venue template is clicked
  useEffect(() => {
    resetCntTmplForm();
  }, [activeCntTmpl]);

  async function fetchDataBundle() {
    try {
      const { meta, data } = await paginatePropertyContentTemplates({
        propertyId: activeProperty._id,
        page: paginationFilters.page,
        translations: true,
        ...(paginationFilters.search && { search: paginationFilters.search }),
        ...(paginationFilters.createdFrom && { startDate: paginationFilters.createdFrom }),
        ...(paginationFilters.createdTo && { endDate: paginationFilters.createdTo }),
        ...(paginationFilters?.translated !== undefined && {
          translated: paginationFilters.translated,
        }),
        ...(paginationFilters?.published !== undefined && {
          published: paginationFilters.published,
        }),
      });

      setPaginationMetadata(meta);

      const contentTemplates =
        paginationFilters.page > 1 ? [...(dataBundle?.contentTemplates ?? []), ...data] : data;

      setDataBundle({
        contentTemplates,
      });
    } finally {
      setLoading(false);
      setLoadingTranslations(false);
    }
  }

  function resetCntTmplForm() {
    const transforms = activeCntTmpl
      ? makeContentTranslationTransform(
          languages.map(l => l.key),
          activeCntTmpl?.translations
        )
      : undefined;

    if (!transforms) {
      setCntTmplTranslForm(undefined);
      return;
    }

    const formData: ContentLibraryTranslationForm = {
      dirty: false,
      transforms,
    };

    setCntTmplTranslForm(formData);
  }

  function changeCntTmplForm(lang: string, change: ChangeContentTemplateFormAction) {
    if (!cntTmplTranslForm) return;

    const updatedForm = { ...cntTmplTranslForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setCntTmplTranslForm(updatedForm);
  }

  async function handleSubmitVenTmplForm() {
    if (
      !cntTmplTranslForm ||
      !activeCntTmpl ||
      !dataBundle ||
      !canModifyMultiPropertyItem(user, serverInfo, activeProperty._id, activeCntTmpl)
    )
      return;

    const translation = generateDefaultNewTranslation(activeCntTmpl._id, 'contenttemplate');

    translation.transforms = cntTmplTranslForm.transforms;

    const response = await replaceTranslationByRefer(activeCntTmpl._id, translation);
    const active = { ...activeCntTmpl, translations: response as ContentTemplateTranslation };
    const idx = dataBundle.contentTemplates.findIndex(ct => ct._id === active._id);
    dataBundle.contentTemplates[idx] = active;
    // No need to refresh bundle, just update the existing list
    setActiveCntTmpl(active);
    setDataBundle({ ...dataBundle });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="content" property={activeProperty} />;
  }

  function leftFn() {
    const contentTemplatesWithTranslations = dataBundle?.contentTemplates || [];
    const loadMoreTranslations =
      paginationMetadata && paginationMetadata.current_page < paginationMetadata.last_page;

    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleDebounce
            onTrigger={[
              (search: string) => setPaginationFilters({ ...paginationFilters, page: 1, search }),
              500,
            ]}
          >
            {({ debounce }) => (
              <BubbleInput
                type="text"
                block
                appearance="bare"
                placeholder="Search..."
                className="flex-grow"
                inputStyle={{ height: '49px' }}
                inputClassName="px-5"
                value={paginationFilters.search}
                onValue={debounce}
              />
            )}
          </BubbleDebounce>
        </div>

        <div style={{ paddingTop: '50px' }}>
          {contentTemplatesWithTranslations.map(ctmpl => {
            const cntStatus = getMultiPropertyKind(ctmpl);

            return (
              <div
                key={ctmpl._id}
                className={`list-item px-5 py-3 cursor-pointer ${
                  activeCntTmpl && safeIdCompare(activeCntTmpl._id, ctmpl._id) ? 'active' : ''
                }`}
                onClick={() => setActiveCntTmpl(ctmpl)}
              >
                <div className="flex items-center">
                  <div className="flex-grow min-w-0">
                    <BubbleBadge variant={cntStatus.variant}>{cntStatus.status}</BubbleBadge>
                    <div className="list-item-name font-semibold overflow-ellipsis">
                      {ctmpl.name}
                    </div>
                  </div>
                  <FontAwesomeIcon
                    icon="chevron-right"
                    className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                  />
                </div>
              </div>
            );
          })}

          {contentTemplatesWithTranslations.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No content templates found
            </div>
          )}

          {loadingTranslations && (
            <BubbleOverlay
              loading
              style={{ minHeight: '200px', marginTop: 'calc(40px + 0.75rem)' }}
              className="shadow rounded"
            />
          )}

          {loadMoreTranslations && (
            <PaginateTranslations
              type="Content"
              onLoadMoreClick={() =>
                setPaginationFilters({
                  ...paginationFilters,
                  page: paginationFilters.page + 1,
                })
              }
            />
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeCntTmpl && cntTmplTranslForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyMultiPropertyItem(user, serverInfo, activeProperty._id, activeCntTmpl) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This content cannot be modified. It may be a brand level event or you lack library
                  permissions.
                </BubbleAlert>
              )}
              {isNotBasicContentTemplate(activeCntTmpl) && (
                <>
                  <TranslationBlock
                    label="Title"
                    value={activeCntTmpl.name}
                    languages={selectedLanguages}
                  >
                    {({ language }) => (
                      <div
                        className={`flex items-start clt-editor-block ${
                          !cntTmplTranslForm.transforms[language.key].active ? 'disable-editor' : ''
                        }`}
                      >
                        <BubbleButton
                          inverse
                          variant={
                            !cntTmplTranslForm.transforms[language.key].active ? 'smoke' : 'navy'
                          }
                          className="px-1-i pt-2 mr-2 leading-none"
                          onClick={() =>
                            changeCntTmplForm(language.key, {
                              active: !cntTmplTranslForm.transforms[language.key].active,
                            })
                          }
                          icon={
                            cntTmplTranslForm.transforms[language.key].active ? 'eye' : 'eye-slash'
                          }
                        />
                        <BubbleInput
                          block
                          type="text"
                          className="flex-grow"
                          inputClassName={
                            !cntTmplTranslForm.transforms[language.key].active
                              ? 'italic text-smoke'
                              : ''
                          }
                          value={cntTmplTranslForm.transforms[language.key].delta.name}
                          onValue={val =>
                            changeCntTmplForm(language.key, {
                              valueChange: { key: 'name', value: val },
                            })
                          }
                        />
                      </div>
                    )}
                  </TranslationBlock>
                  <TranslationBlock
                    label="Description"
                    value={activeCntTmpl.description}
                    languages={selectedLanguages}
                    headerCharCountFn={val =>
                      val.value && (
                        <span className="text-smoke-light mr-auto">
                          ({striptags(val.value).length})
                        </span>
                      )
                    }
                    headerValueFn={val => (
                      <div className="p-3 border bg-white shadow-md">
                        {val.value && <div dangerouslySetInnerHTML={{ __html: val.value }} />}
                      </div>
                    )}
                    charCountFn={({ language }) => (
                      <span className="text-smoke-light">
                        (
                        {
                          striptags(cntTmplTranslForm.transforms[language.key].delta.description)
                            .length
                        }
                        )
                      </span>
                    )}
                  >
                    {({ language }) => (
                      <div
                        className={`flex items-start clt-editor-block ${
                          !cntTmplTranslForm.transforms[language.key].active ? 'disable-editor' : ''
                        }`}
                      >
                        <BubbleButton
                          inverse
                          variant={
                            !cntTmplTranslForm.transforms[language.key].active ? 'smoke' : 'navy'
                          }
                          className="px-1-i pt-2 mr-2 leading-none"
                          onClick={() =>
                            changeCntTmplForm(language.key, {
                              active: !cntTmplTranslForm.transforms[language.key].active,
                            })
                          }
                          icon={
                            cntTmplTranslForm.transforms[language.key].active ? 'eye' : 'eye-slash'
                          }
                        />
                        <div className="flex-grow">
                          <Editor
                            value={cntTmplTranslForm.transforms[language.key].delta.description}
                            onValue={val => {
                              changeCntTmplForm(language.key, {
                                valueChange: { key: 'description', value: val },
                              });
                            }}
                            mode="text-link"
                          />
                        </div>
                      </div>
                    )}
                  </TranslationBlock>
                </>
              )}
              <TranslationBlock
                label="Print Messaging"
                value={activeCntTmpl.content}
                languages={selectedLanguages}
                headerCharCountClassName="flex items-center"
                headerCharCountFn={val =>
                  val.value ? (
                    <>
                      <span className="text-smoke-light mr-auto">
                        ({striptags(val.value).length})
                      </span>
                      <div>
                        <span>Show HTML</span>
                        <BubbleInput
                          checked={isAdvanced}
                          onValue={setIsAdvanced}
                          type="checkbox"
                          className="ml-1"
                          inputClassName="shadow-none-i"
                        />
                      </div>
                    </>
                  ) : null
                }
                headerValueFn={val => (
                  <div className="p-3 border bg-white shadow-md">
                    {val.value && (
                      <>
                        {isAdvanced ? (
                          <div>{val.value}</div>
                        ) : (
                          <div dangerouslySetInnerHTML={{ __html: val.value }} />
                        )}
                      </>
                    )}
                  </div>
                )}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({striptags(cntTmplTranslForm.transforms[language.key].delta.content).length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div
                    className={`flex items-start clt-editor-block ${
                      !cntTmplTranslForm.transforms[language.key].active ? 'disable-editor' : ''
                    }`}
                  >
                    <BubbleButton
                      inverse
                      variant={
                        !cntTmplTranslForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeCntTmplForm(language.key, {
                          active: !cntTmplTranslForm.transforms[language.key].active,
                        })
                      }
                      icon={cntTmplTranslForm.transforms[language.key].active ? 'eye' : 'eye-slash'}
                    />
                    {isAdvanced ? (
                      <BubbleTextArea
                        block
                        className="flex-grow"
                        textAreaClassName={
                          !cntTmplTranslForm.transforms[language.key].active
                            ? 'italic text-smoke'
                            : ''
                        }
                        value={cntTmplTranslForm.transforms[language.key].delta.content}
                        onValue={val =>
                          changeCntTmplForm(language.key, {
                            valueChange: { key: 'content', value: val },
                          })
                        }
                      />
                    ) : (
                      <div className="flex-grow">
                        <Editor
                          value={cntTmplTranslForm.transforms[language.key].delta.content}
                          onValue={val =>
                            changeCntTmplForm(language.key, {
                              valueChange: { key: 'content', value: val },
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </TranslationBlock>
            </div>
            <div className="sticky bottom-0 p-3 bg-eggshell border-t z-10">
              <BubbleButton
                variant="navy"
                onClick={() => handleSubmitVenTmplForm()}
                disabled={
                  !cntTmplTranslForm.dirty ||
                  !canModifyMultiPropertyItem(user, serverInfo, activeProperty._id, activeCntTmpl)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!cntTmplTranslForm.dirty}
                inverse
                onClick={() => resetCntTmplForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (cntTmplTranslForm && cntTmplTranslForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={cntTmplTranslForm && cntTmplTranslForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="content"
        showLibraryFilters
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
        filters={paginationFilters}
        setFilters={setPaginationFilters}
      />
    </>
  );
}
